import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  CollectorDTO,
  SiteByIdDTO,
  getAllCollectors,
  getCollectorsBySiteId,
  getDeActivatedCollectorsBySiteId,
  getSiteById,
  getUnitCostByCollectorId,
} from "api";

import { QUERY_KEYS } from "consts";

import { MaybeNull } from "types";

export const useCollectorsBySiteId = (siteId: MaybeNull<number>) =>
  useQuery<CollectorDTO[]>(
    [QUERY_KEYS.COLLECTORS, QUERY_KEYS.SITES, siteId],
    () => getCollectorsBySiteId(siteId!),
    {
      enabled: !!siteId,
    },
  );

export const useAllCollectors = (enabled: boolean = true) =>
  useQuery<CollectorDTO[]>([QUERY_KEYS.COLLECTORS_ALL], getAllCollectors, {
    enabled,
  });

export const useDeActivatedCollectorsBySiteId = (siteId: MaybeNull<number>) =>
  useQuery<CollectorDTO[]>(
    [QUERY_KEYS.COLLECTORS, 'deactivated', QUERY_KEYS.SITES, siteId],
    () => getDeActivatedCollectorsBySiteId(siteId!),
    {
      enabled: !!siteId,
    },
  );

export const useUnitCostByCollectorId = (collectorId: MaybeNull<number>) =>
  useQuery<any>(
    [QUERY_KEYS.COLLECTORS, QUERY_KEYS.SITES, collectorId],
    () => getUnitCostByCollectorId(collectorId!),
  );

export const useCollectorsTableData = (siteId: MaybeNull<number>) => {
  const { data: site, isFetching: isSiteFetching } = useQuery<SiteByIdDTO>(
    [QUERY_KEYS.SITES, siteId],
    () => getSiteById(siteId!),
    { enabled: !!siteId },
  );
  const { data: deactivatedCollectors = [], isFetching: isDeactivatedFetching } =
    useDeActivatedCollectorsBySiteId(siteId);

  const allCollectorsData = useMemo(() => {
    const activeCollectors: any[] = (site as any)?.collectors ?? [];
    const deactivatedIds = new Set(deactivatedCollectors.map((c: any) => c.id));
    return [
      ...activeCollectors.filter((c: any) => !deactivatedIds.has(c.id)),
      ...deactivatedCollectors.map((c: any) => ({ ...c, isDeactivated: true })),
    ].sort((a: any, b: any) => {
      if (a.disableDate === null && b.disableDate !== null) return -1;
      if (a.disableDate !== null && b.disableDate === null) return 1;
      return 0;
    });
  }, [site, deactivatedCollectors]);

  return {
    allCollectorsData,
    isFetching: (isSiteFetching ?? false) || (isDeactivatedFetching ?? false),
  };
};
